import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import { getTenant, updateTenant, getLdapCredentials, updateLdapCredentials } from "../../api/Tenants";
import toast from "react-hot-toast";
import Breadcrumbs from "../../components/Breadcrumbs";
import EditForm from "../../components/EditForm";
import Dropdown from "../../components/Dropdown";
import InputField from "../../components/InputField";
import Switch from "../../components/Switch";
import Modal from "../../components/Modal";
import ScoCustomQuestionsSection from "../../components/tenant/ScoCustomQuestionsSection";
import ScoListValuesSection from "../../components/tenant/ScoListValuesSection";

const EditTenantPage = () => {
  const { getAccessToken } = useAuth();
  const { tenantPublicId, user } = useParams();
  const navigate = useNavigate();
  const [originalTenant, setOriginalTenant] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loadingTenant, setLoadingTenant] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [loadingLdapCredentials, setLoadingLdapCredentials] = useState(true);
  const [ldapCredentials, setLdapCredentials] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (tenantPublicId) {
      handleGetTenant();
      handleGetLdapCredentials();
    }
  }, [tenantPublicId]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleGetLdapCredentials = async () => {
    try {
      const accessToken = await getAccessToken();
      const ldapCredentials = await getLdapCredentials(accessToken);
      setLdapCredentials(ldapCredentials);
      console.log("LDAP Credentials:", ldapCredentials);
    } catch (error) {
      console.error("Failed to fetch LDAP credentials:", error);
    } finally {
      setLoadingLdapCredentials(false);
    }
  };

  const handleGetTenant = async () => {
    try {
      const accessToken = await getAccessToken();
      const newTenant = await getTenant(tenantPublicId, accessToken);
      setTenant(newTenant);
      setOriginalTenant(newTenant);
    } catch (error) {
      console.error("Failed to fetch tenant:", error);
    } finally {
      setLoadingTenant(false);
    }
  };

  useEffect(() => {
    if (tenant && originalTenant) {
      const isEqual = JSON.stringify(tenant) === JSON.stringify(originalTenant);
      setHasChanges(!isEqual);
    }
  }, [tenant, originalTenant]);

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleEditLdap = async () => {
    if (!tenant?.ldapCredentialPublicId) {
      toast.error("Please fill in LDAP credentials.");
      return;
    }
    try {
      const accessToken = await getAccessToken();
      const ldapData = {
        ldapCredentialPublicId: tenant?.ldapCredentialPublicId || "",
        ldapUsername: tenant?.ldapUsername,
        ldapPassword: tenant?.ldapPassword,
      };

      await updateLdapCredentials(ldapData, accessToken);
      
      toast.success(
        "Ldap was successfully edited!",
        {
          iconTheme: {
            primary: "#4CAF50",
            secondary: "#DFF2BF",
          },
        }
      );
      handleGetTenant();
      handleGetLdapCredentials([]);
      setIsModalOpen(false);
    } catch (error) {
      console.error("Failed to update Ldap:", error);
      toast.error("Failed to update LDAP.");
    }
  };

  /*   const handleSaveTenant = async () => {
    try {
      const accessToken = await getAccessToken();
      await updateTenant(tenant, accessToken);
      toast.success("Tenant updated successfully");
      setHasChanges(false);
    } catch (error) {
      toast.error("Failed to update tenant");
    }
  }; */

  const handleSaveTenant = async () => {
    console.log("🚀 Tenant Object Before Update:", tenant);

    try {
      const accessToken = await getAccessToken();

      const baseTenant = {
        ...tenant,
        tenantPublicId,
        // The input field yields a string; the API expects a number or null
        // (empty field = no Ingram connection).
        ingramCustomerId:
          tenant.ingramCustomerId === "" || tenant.ingramCustomerId == null
            ? null
            : Number(tenant.ingramCustomerId),
        entraTenantId: tenant.entraTenantId?.trim() || "",
        updatedBy: user?.username || "System",
        updatedDateTimeUTC: new Date().toISOString(),
      };

      let updatedTenant;

      if (tenant.ldapCredentialPublicId) {
        updatedTenant = {
          ...baseTenant,
          ldapCredentialPublicId: tenant.ldapCredentialPublicId,
        };
      } else {
        updatedTenant = {
          ...baseTenant,
          ldapUsername: tenant.ldapUsername,
          ldapPassword: tenant.ldapPassword,
          ldapDomain: tenant.ldapDomain,
          ldapCredentialPublicId: null,
          ldapSyncEnabled: tenant.isLdapSyncEnabled,
        };
      }
      
      const updateResponse = await updateTenant(updatedTenant, accessToken);

      console.log("Update Tenant Response:", updateResponse);

      toast.success("Tenant updated successfully", {
        iconTheme: {
          primary: "#4CAF50",
          secondary: "#DFF2BF",
        },
      });

      window.dispatchEvent(new Event("tenantUpdated"));

      setHasChanges(false);
    } catch (error) {
      console.error("Update error:", error.response?.data || error.message);
      toast.error(
        `Error: ${error.response?.data?.message || "Failed to update tenant"}`
      );
    }
  };
  
  const isLdapReadOnly = !!tenant?.ldapCredentialPublicId;

  const ldapCredentialOptions = [
    ...ldapCredentials.map((cred) => cred.ldapUsername),
    "New LDAP"
  ];

  const selectedLdapValue =
  tenant?.ldapCredentialPublicId
    ? ldapCredentials.find(c => c.ldapCredentialPublicId === tenant.ldapCredentialPublicId)?.ldapUsername
    : "New LDAP";

  const handleLdapCredentialSelect = (selectedOption) => {
    if (selectedOption === "New LDAP") {
      setTenant((prevData) => ({
        ...prevData,
        ldapCredential: "New LDAP",
        ldapCredentialPublicId: null,
        ldapUsername: "",
        ldapPassword: "",
      }));
    }else {
      const selectedCredential = ldapCredentials.find((cred) => cred.ldapUsername === selectedOption);
      if (selectedCredential) {
        setTenant((prevData) => ({
          ...prevData,
          ldapCredentialPublicId: selectedCredential.ldapCredentialPublicId,
          ldapUsername: selectedCredential.ldapUsername,
          ldapPassword: selectedCredential.ldapPassword,
        }));
      }
    }}

  console.log("TENANT:", tenant);

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className={`${loadingTenant ? "loading" : ""} page-content`}>
            <Breadcrumbs />

            <div className="flex flex-row items-center pb-4">
              <button onClick={handleBackClick} className="text-black flex">
                <FontAwesomeIcon icon={faArrowLeftLong} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4">
                Edit Tenant: {tenant?.displayName || "Unknown"}
              </h2>
              <div className="flex gap-4 ml-auto">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleBackClick}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!hasChanges}
                  onClick={handleSaveTenant}
                >
                  Update tenant
                </button>
              </div>
            </div>
            <div className="flex flex-col px-4 py-8 shadow-lg border rounded-md w-full gap-6">
              <EditForm
                data={tenant || {}}
                setData={setTenant}
                setHasChanges={setHasChanges}
                disabled={false}
                fields={[
                  {
                    key: "tenantPublicId",
                    label: "Tenant ID",
                    type: "text",
                    disabled: true,
                  },
                  {
                    key: "updatedDateTimeUTC",
                    label: "Updated date",
                    type: "date",
                    disabled: true,
                  },
                  {
                    key: "entraTenantId",
                    label: "Entra ID",
                    type: "text",
                    disabled: false,
                  },
                  {
                    key: "ingramCustomerId",
                    label: "Ingram ID",
                    type: "number",
                    disabled: false,
                  },
                  {
                    key: "displayName",
                    label: "Name",
                    type: "text",
                  },
                  {
                    key: "domain",
                    label: "Domain",
                    type: "text",
                    disabled: false,
                  },
                  {
                    key: "isEntraSyncEnabled",
                    label: "Entra sync",
                    type: "switch",
                    disabled: false,
                  },
                  {
                    key: "isEnabled",
                    label: "Enable tenant",
                    type: "switch",
                    disabled: false,
                  },
                  {
                    key: "agreementPublicId",
                    label: "Agreement ID",
                    type: "text",
                    disabled: true,
                  },
                  {
                    key: "createdBy",
                    label: "Created by",
                    type: "text",
                    disabled: true,
                  },
                  {
                    key: "createdDateTimeUTC",
                    label: "Created date",
                    type: "date",
                    disabled: true,
                  },
                  {
                    key: "updatedBy",
                    label: "Updated by",
                    type: "text",
                    disabled: true,
                  },
                ]}
              />
              <div className="flex flex-col border-t">
                <div className="flex flex-row items-center py-4">
                  <h2 className="">LDAP settings</h2>
                  <div className="flex gap-4 ml-auto">
                    <button
                      className="btn btn-primary-small"
                      disabled={!tenant?.ldapCredentialPublicId}
                      onClick={handleOpenModal}
                      >
                      Update LDAP settings
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-6 w-full">
                  
                  <Dropdown
                    label="LDAP credential"
                    value={[selectedLdapValue]}
                    options={ldapCredentialOptions}
                    onSelect={handleLdapCredentialSelect}
                    disabled={false}
                  />

                  <InputField
                    label="LDAP domain"
                    value={tenant?.ldapDomain || ""}
                    onChange={(e) => {
                      setTenant((prevData) => ({
                        ...prevData,
                        ldapDomain: e.target.value
                      }))
                      setHasChanges(true)
                    }}
                    disabled={false}
                    type="text"
                  />

                  <InputField
                    label="LDAP username"
                    value={tenant?.ldapUsername || ""}
                    onChange={(e) => {
                      setTenant((prevData) => ({
                        ...prevData,
                        ldapUsername: e.target.value
                      }))
                      setHasChanges(true)
                    }}
                    disabled={isLdapReadOnly}
                    type="text"
                  />

                  <InputField
                    label="LDAP password"
                    value={tenant?.ldapPassword || ""}
                    onChange={(e) => {
                      setTenant((prevData) => ({
                        ...prevData,
                        ldapPassword: e.target.value
                      }))
                      setHasChanges(true)
                    }}
                    disabled={isLdapReadOnly}
                    type="password"
                  />

                  <div className="flex flex-col w-1/2">
                    <label className="text-sm font-bold pb-2">LDAP sync</label>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={tenant?.isLdapSyncEnabled || false}
                        onChange={(checked) => {
                          setTenant((prevData) => ({
                            ...prevData,
                            isLdapSyncEnabled: checked,
                          }))
                          setHasChanges(true)
                        }}
                        disabled={false}
                      />
                      <p className="text-sm">
                        {tenant?.isLdapSyncEnabled ? "Enabled" : "Disabled"}
                      </p>
                    </div>
                  </div>

                </div>
              </div>
              <div className="flex flex-col border-t">
                <h2 className="my-6">Registrations</h2>
                <div className="ml-4 flex flex-col">
                  <h3 className="my-2 hover:text-primary-400 text-base w-fit">
                    <Link to={tenant?.entraInsightApiRegistrationUrl}>
                      Register Diflex Insight DIP API in customer Entra
                    </Link>
                  </h3>
                  <h3 className="my-2 hover:text-primary-400 text-base w-fit">
                    <Link to={tenant?.entraInsightDIPAppRegistrationUrl}>
                      Register Diflex Insight DIP App in customer Entra
                    </Link>
                  </h3>
                  <h3 className="my-2 hover:text-primary-400 text-base w-fit">
                    <Link to={tenant?.entraInsightSyncAppRegistrationUrl}>
                      Register Diflex Insight Synchronisation Job in customer
                      Entra
                    </Link>
                  </h3>
                </div>
              </div>

              {/* PBI #320: the tenant's customer-specific SCO form questions. */}
              <ScoCustomQuestionsSection tenantPublicId={tenantPublicId} />

              {/* #357: the Office, Company and Department values its onboarding form offers. */}
              <ScoListValuesSection tenantPublicId={tenantPublicId} />
            </div>
          </div>
        </main>
      </div>
      <Modal isOpen={isModalOpen} setIsOpen={setIsModalOpen}>
        <div className="bg-white p-6 rounded-lg w-[400px]">
          <h2 className="text-xl font-semibold mb-4">Edit Ldap credentials</h2>
          <div className="flex flex-col gap-4">
            <InputField
              label="LDAP username"
              value={tenant?.ldapUsername || ""}
              onChange={(value) =>
                setTenant((prevData) => ({
                  ...prevData,
                  ldapUsername: value,
                }))
              }
              disabled={false}
              type="text"
            />

            <InputField
              label="LDAP password"
              value={tenant?.ldapPassword || ""}
              onChange={(e) => {
                setTenant((prevData) => ({
                  ...prevData,
                  ldapPassword: e.target.value
                }))
                setHasChanges(true)
              }}
              disabled={false}
              type="text"
            />
          </div>

          <div className="flex justify-end mt-4 gap-4">
            <button
              className="btn-outline-primary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </button>
            <button className="btn-primary" onClick={handleEditLdap}>
              Confirm
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default EditTenantPage;
